import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import useIsMobile from '../../hooks/useIsMobile';

/** Pricing page — animated 3D wave mesh.
 *  Gradient violet→cyan→magenta. Mouse moves a ripple centre.
 */
const WaveMeshBg = () => {
    const mountRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile) return;
        const mount = mountRef.current;
        if (!mount) return;
        const w = mount.clientWidth, h = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(55, w / h, 0.1, 200);
        camera.position.set(0, 16, 22);
        camera.lookAt(0, 0, 0);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        // Plane geometry — high subdivision for smooth waves
        const SIZE = 60, SEG = 90;
        const geo = new THREE.PlaneGeometry(SIZE, SIZE, SEG, SEG);
        geo.rotateX(-Math.PI / 2);

        // Per-vertex colour gradient: violet edges → cyan centre with magenta peaks (set dynamically)
        const colors = new Float32Array(geo.attributes.position.count * 3);
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const mat = new THREE.MeshBasicMaterial({
            vertexColors: true,
            wireframe: true,
            transparent: true,
            opacity: 0.95,
        });
        const mesh = new THREE.Mesh(geo, mat);
        scene.add(mesh);

        // Filled plane underneath (lower opacity) for depth
        const fillMat = new THREE.MeshBasicMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 0.18,
            side: THREE.DoubleSide,
        });
        const fillMesh = new THREE.Mesh(geo, fillMat);
        scene.add(fillMesh);

        // Mouse target on plane
        const mouse = { x: 0, z: 0, tx: 0, tz: 0 };
        const onMouseMove = (e) => {
            const rect = mount.getBoundingClientRect();
            const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
            mouse.tx = nx * SIZE * 0.5;
            mouse.tz = ny * SIZE * 0.5;
        };
        window.addEventListener('mousemove', onMouseMove);

        const onResize = () => {
            const W = mount.clientWidth, H = mount.clientHeight;
            renderer.setSize(W, H);
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', onResize);

        const colVio = new THREE.Color(0x7B61FF);
        const colCyan = new THREE.Color(0x00E5CC);
        const colMag = new THREE.Color(0xFF3D8A);

        const clock = new THREE.Clock();
        let raf = 0;
        const tmpCol = new THREE.Color();
        const tick = () => {
            raf = requestAnimationFrame(tick);
            const t = clock.getElapsedTime();
            mouse.x += (mouse.tx - mouse.x) * 0.06;
            mouse.z += (mouse.tz - mouse.z) * 0.06;

            const pos = geo.attributes.position;
            for (let i = 0; i < pos.count; i++) {
                const x = pos.getX(i);
                const z = pos.getZ(i);
                // Layered sine waves
                let y =
                    Math.sin(x * 0.18 + t * 0.9) * 0.85 +
                    Math.cos(z * 0.22 + t * 1.1) * 0.85 +
                    Math.sin((x + z) * 0.14 + t * 0.6) * 0.5;
                // Cursor ripple
                const dx = x - mouse.x;
                const dz = z - mouse.z;
                const dist = Math.sqrt(dx * dx + dz * dz);
                y += Math.sin(dist * 0.5 - t * 4) * Math.max(0, 1 - dist / 14) * 1.4;
                pos.setY(i, y);

                // Colour: centre = cyan, edges = violet, peaks = magenta
                const distCentre = Math.sqrt(x * x + z * z) / (SIZE * 0.5);
                tmpCol.copy(colVio).lerp(colCyan, 1 - distCentre);
                if (y > 1.2) tmpCol.lerp(colMag, Math.min(1, (y - 1.2) * 0.7));
                colors[i * 3] = tmpCol.r;
                colors[i * 3 + 1] = tmpCol.g;
                colors[i * 3 + 2] = tmpCol.b;
            }
            pos.needsUpdate = true;
            geo.attributes.color.needsUpdate = true;

            renderer.render(scene, camera);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', onResize);
            if (mount && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            geo.dispose();
            mat.dispose();
            fillMat.dispose();
            renderer.dispose();
        };
    }, [isMobile]);

    if (isMobile) {
        return (
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'linear-gradient(180deg, transparent 0%, rgba(37,99,235,0.12) 60%, rgba(56,189,248,0.18) 100%)',
                }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            ref={mountRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
            data-testid="bg-wave-mesh"
        />
    );
};

export default WaveMeshBg;
